import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Mail, MessageCircle, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSettings, waLink } from "@/context/AppContext";
import SEO from "@/components/SEO";
import { PageHero, Reveal } from "@/components/Motion";

const inputCls = "w-full border border-forest/15 bg-white px-4 py-3 text-sm text-forest placeholder:text-forest/35 focus:border-gold focus:outline-none transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.22em] text-forest/60 mb-1.5";

export default function Contact() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", company: "", country: "", email: "", phone: "", product_interest: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  if (!settings) return <div className="min-h-screen bg-parchment" />;
  const biz = settings.business || {};
  const seo = settings.seo?.pages?.contact;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO title={seo?.title} description={seo?.description} path="/contact" />
      <PageHero eyebrow="Contact Us" title="Let's talk about your requirements." description="Reach our export team by phone, WhatsApp or email — or send the form below and we will respond to your inquiry." image={settings.categories?.tea?.image} />

      <section className="bg-parchment py-16 lg:py-24">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-6">
            <Reveal>
              <div className="border border-forest/10 bg-white p-8" data-testid="contact-info-card">
                <p className="font-serif text-2xl text-forest">{biz.public_name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold-dark">{biz.legal_name}</p>
                <div className="mt-6 space-y-5 text-sm">
                  <p className="flex items-start gap-3 text-[#4A554D]" data-testid="contact-address">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-gold-dark" />
                    <span><span className="font-semibold text-forest">Registered Office</span><br />{biz.address}</span>
                  </p>
                  {(biz.phones || []).map((p) => (
                    <a key={p.tel} href={`tel:${p.tel}`} data-testid={`contact-phone-${p.tel}`} className="flex items-center gap-3 text-forest hover:text-gold-dark transition-colors">
                      <Phone className="h-4 w-4 shrink-0 text-gold-dark" /> {p.display}
                    </a>
                  ))}
                  <a href={`mailto:${biz.email}`} data-testid="contact-email" className="flex items-center gap-3 text-forest hover:text-gold-dark transition-colors">
                    <Mail className="h-4 w-4 shrink-0 text-gold-dark" /> {biz.email}
                  </a>
                </div>
                <div className="mt-7 grid grid-cols-2 gap-2.5">
                  <a href={`tel:${biz.phones?.[0]?.tel || "+919849966082"}`} data-testid="contact-call-button" className="inline-flex items-center justify-center gap-2 bg-forest px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-parchment hover:bg-forest-leaf transition-colors">
                    <Phone className="h-3.5 w-3.5" /> Call Us
                  </a>
                  <a href={waLink(settings)} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button" className="inline-flex items-center justify-center gap-2 border border-forest/20 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest hover:border-gold hover:text-gold-dark transition-colors">
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp Us
                  </a>
                  <a href={`mailto:${biz.email}`} data-testid="contact-email-button" className="inline-flex items-center justify-center gap-2 border border-forest/20 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest hover:border-gold hover:text-gold-dark transition-colors">
                    <Mail className="h-3.5 w-3.5" /> Email Us
                  </a>
                  <Link to="/quote" data-testid="contact-quote-button" className="inline-flex items-center justify-center gap-2 bg-gold px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-forest hover:bg-gold-light transition-colors">
                    Request a Quote <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="overflow-hidden border border-forest/10 bg-white" data-testid="contact-map">
                <iframe
                  title="AnVibes Exports registered office location"
                  src="https://www.google.com/maps?q=Dharmapuri,+Jagtial+District,+Telangana+505425,+India&output=embed"
                  className="h-64 w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <div className="border border-forest/10 bg-white p-8 lg:p-10">
              <h2 className="font-serif text-3xl text-forest">Send us a message</h2>
              <p className="mt-2 text-sm text-[#4A554D]">We typically respond to B2B inquiries promptly during business hours (IST).</p>
              {sent ? (
                <div className="mt-8 border border-gold/40 bg-forest/5 p-8 text-center" data-testid="contact-success-message">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-gold-dark" />
                  <p className="mt-4 font-serif text-xl text-forest">Thank you for your message.</p>
                  <p className="mt-2 text-sm text-[#4A554D]">Our team will get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="contact-form">
                  <input type="text" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <div>
                    <label className={labelCls} htmlFor="cf-name">Name *</label>
                    <input id="cf-name" data-testid="contact-field-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-company">Company</label>
                    <input id="cf-company" data-testid="contact-field-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Company name" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-country">Country</label>
                    <input id="cf-country" data-testid="contact-field-country" className={inputCls} value={form.country} onChange={set("country")} placeholder="Your country" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-email">Email *</label>
                    <input id="cf-email" data-testid="contact-field-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-phone">Phone</label>
                    <input id="cf-phone" data-testid="contact-field-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="cf-interest">Product Interest</label>
                    <select id="cf-interest" data-testid="contact-field-interest" className={inputCls} value={form.product_interest} onChange={set("product_interest")}>
                      <option value="">Select interest</option>
                      <option value="Rice">Rice</option>
                      <option value="Indian Spices">Indian Spices</option>
                      <option value="Tea">Tea</option>
                      <option value="Multiple / Other">Multiple / Other</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="cf-message">Message</label>
                    <textarea id="cf-message" data-testid="contact-field-message" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Tell us about your requirements..." />
                  </div>
                  <div className="sm:col-span-2">
                    <button type="submit" disabled={sending} data-testid="contact-form-submit-button" className="inline-flex items-center gap-3 bg-forest px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-parchment transition-colors hover:bg-forest-leaf disabled:opacity-60">
                      {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                      {sending ? "Sending..." : "Send Message"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
